/**
 * FoundationYearLanding — `/pathways/foundation-year` explainer.
 *
 * Serves the anxious near-miss cohort: A-level students who didn't
 * hit their offer, mature learners returning to study, and international
 * applicants whose home qualifications don't meet direct-entry bar.
 * All of them share the same question: "if I can't get in the front
 * door, is there another way?" — and foundation year is the answer
 * for hundreds of UK degrees.
 *
 * Deep-links straight into `/advanced?level=foundation` with the
 * backend `foundation_year_only=true` filter applied.
 */
import { Link } from "react-router-dom";
import {
  ArrowRight,
  GraduationCap,
  Compass,
  ShieldCheck,
  Sparkles,
  Clock,
  Building2,
  Users,
} from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";

export default function FoundationYearLanding() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="foundation-year-landing">
      <PathwaySEO
        title="Foundation-year degrees — the near-miss route into a UK bachelor's"
        description="Missed your A-level offer, coming back to study after time out, or your home qualifications don't map onto UCAS Tariff? A UK foundation year is a Year 0 attached to a full bachelor's, and 210+ courses offer it."
        path="/pathways/foundation-year"
      />
      {/* Hero */}
      <div>
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Sparkles className="h-3 w-3" /> Pathway explainer
        </div>
        <h1
          data-testid="foundation-year-heading"
          className="mt-1 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          The foundation-year route
        </h1>
        <p className="mt-3 text-stone-700 max-w-3xl leading-relaxed text-lg">
          What if you didn't hit your A-level offer, are coming back to
          study after 10 years out, or your home qualifications don't map
          onto UCAS Tariff points?
        </p>
        <p className="mt-3 text-stone-600 max-w-3xl leading-relaxed">
          A foundation year — sometimes called "Year 0" or "Integrated
          Foundation" — is a full extra year attached to the front of a
          UK bachelor's degree. You enrol at the university, live in
          student accommodation, sit modules that top up your subject
          knowledge, and then progress directly into Year 1 of the full
          degree. No reapplying, no gap. Roughly 210 UK courses in our
          catalogue offer this route.
        </p>
      </div>

      {/* CTA */}
      <div className="mt-6 rounded-lg border border-emerald-300 bg-emerald-50/60 p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-editorial text-xl text-[#002147]">
            See every course with a foundation year
          </div>
          <div className="text-[13px] text-stone-600 mt-1">
            Runs the full 6-layer advanced match against your profile,
            then filters to only degrees with a validated foundation-year
            entry route.
          </div>
        </div>
        <Link
          to="/advanced?level=foundation"
          data-testid="foundation-year-cta"
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-emerald-800"
        >
          Match me to foundation-year degrees <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Who's it for? */}
      <div className="mt-10">
        <div className="label-uppercase text-[#4C1D95] font-semibold">
          Who this route is for
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Persona
            icon={<Users className="h-4 w-4" />}
            title="A-level near-misses"
            body={
              "You applied for a 3-year degree, missed one grade on results " +
              "day, and Clearing didn't get you into the same subject. A " +
              "foundation year attached to that same university (often the " +
              "same course code + F) is the direct workaround."
            }
          />
          <Persona
            icon={<Clock className="h-4 w-4" />}
            title="Mature learners"
            body={
              "You've been out of formal study for 3+ years. Universities " +
              "know the equivalencies don't line up cleanly, so the " +
              "foundation year re-establishes your subject fluency (maths, " +
              "essay writing, lab technique) without you having to redo " +
              "A-levels externally."
            }
          />
          <Persona
            icon={<Building2 className="h-4 w-4" />}
            title="International applicants"
            body={
              "Your home-country school-leaving qualification doesn't " +
              "translate to a direct UCAS Tariff. Foundation year gives " +
              "you a UK-based, UK-graded academic year before Year 1 — " +
              "usually pooled with UK domestic near-misses so the cohort " +
              "isn't segregated."
            }
          />
        </div>
      </div>

      {/* How it works */}
      <div className="mt-10">
        <div className="label-uppercase text-[#4C1D95] font-semibold">
          How it works
        </div>
        <ol className="mt-4 space-y-4">
          <Step
            n={1}
            title="You apply through UCAS to a 4-year integrated degree"
            body={
              "You'll see courses with codes like `H100F` or titles ending " +
              "in `with Foundation Year` or `(with Integrated Foundation)`. " +
              "You apply the same way as any other UCAS course — same form, " +
              "same personal statement. Foundation-year offers are typically " +
              "1-2 A-level grades below the direct-entry offer for the same " +
              "degree."
            }
          />
          <Step
            n={2}
            title="You spend Year 0 on the same campus"
            body={
              "Foundation-year students live in university accommodation, " +
              "sit modules in the same buildings, and use the same libraries " +
              "and gyms. Most timetables cover core subject knowledge (e.g. " +
              "maths for engineering, essay method for humanities) plus study " +
              "skills, plus a preview of Year 1 material."
            }
          />
          <Step
            n={3}
            title="Progression is automatic if you meet the threshold"
            body={
              "Most foundation years require an overall 40%-50% average " +
              "across modules to progress into Year 1 of the linked bachelor. " +
              "No reapplication. Your student finance rolls over into a " +
              "single 4-year loan (foundation year attracts the same " +
              "government-backed loans as the rest of the degree)."
            }
          />
          <Step
            n={4}
            title="You graduate with the same degree as direct-entry students"
            body={
              "The degree certificate does NOT mention foundation year. " +
              "Employers see the same BSc / BEng / BA / MPharm. Alumni " +
              "networks, career services and postgrad progression all treat " +
              "foundation-year cohorts as identical to direct-entry."
            }
          />
        </ol>
      </div>

      {/* Warnings */}
      <div className="mt-10 rounded-lg border border-amber-300 bg-amber-50/40 p-5">
        <div className="label-uppercase text-amber-900 font-semibold flex items-center gap-1.5">
          <ShieldCheck className="h-3 w-3" /> Things families should sanity-check
        </div>
        <ul className="mt-3 space-y-2 text-[13px] text-stone-800 leading-snug">
          <li>
            <strong>Confirm the linked-degree title.</strong> Some FE
            colleges run "foundation degrees" (Level 5, 2-year sub-degree
            HE quals) rather than "foundation years" attached to a full
            bachelor. Both are legitimate — but they're not the same
            product. We only tag `foundation_year: true` for the
            attached-to-a-full-bachelor variant.
          </li>
          <li>
            <strong>Tuition is charged for Year 0.</strong> UK-domestic
            foundation year tuition is capped at £9,535/yr (2025/26).
            International students pay international tuition for the full
            4 years, so the total cost of a foundation-year degree is
            typically 33% higher than the direct-entry equivalent.
          </li>
          <li>
            <strong>Not all subjects have a foundation route.</strong>{" "}
            Medicine, dentistry, veterinary and law have very few foundation
            options and those that exist (e.g. Southampton's BM6, King's
            Extended Medical Degree Programme) are highly selective on
            widening-participation criteria.
          </li>
        </ul>
      </div>

      {/* Final CTA */}
      <div className="mt-10 rounded-lg border border-[#4C1D95]/20 bg-[#4C1D95]/[0.03] p-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="font-editorial text-xl text-[#002147]">
            Ready to see your foundation-year matches?
          </div>
          <div className="text-[13px] text-stone-600 mt-1">
            Complete your profile once — grades, budget, preferred cities —
            and the advanced match runs the full 6-layer engine, filtered
            to foundation-year routes only.
          </div>
        </div>
        <Link
          to="/advanced?level=foundation"
          data-testid="foundation-year-cta-bottom"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#4C1D95] text-white px-4 py-2 text-sm font-underground-sc tracking-wider hover:bg-[#3a1476]"
        >
          Match me to foundation-year degrees <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {/* Also see */}
      <div className="mt-6 text-[13px] text-stone-500 flex items-center gap-2">
        <Compass className="h-3 w-3" />
        Also see:{" "}
        <Link to="/uk-fe-colleges" className="text-[#4C1D95] underline">
          UK FE colleges & specialist HE
        </Link>
        {" · "}
        <Link to="/countries/uk" className="text-[#4C1D95] underline">
          UK cohort claims
        </Link>
      </div>
    </div>
  );
}

function Persona({ icon, title, body }) {
  return (
    <div
      data-testid={`foundation-year-persona-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className="rounded-md border border-stone-200 bg-white p-4"
    >
      <div className="flex items-center gap-2 text-[#4C1D95]">
        {icon}
        <div className="font-editorial text-lg text-[#002147]">{title}</div>
      </div>
      <p className="mt-2 text-[13px] text-stone-700 leading-snug">{body}</p>
    </div>
  );
}

function Step({ n, title, body }) {
  return (
    <li className="flex gap-4">
      <div className="shrink-0 h-8 w-8 rounded-full bg-[#4C1D95] text-white font-mono text-sm flex items-center justify-center">
        {n}
      </div>
      <div className="min-w-0">
        <div className="font-editorial text-lg text-[#002147]">{title}</div>
        <p className="mt-1 text-[13px] text-stone-700 leading-snug">{body}</p>
      </div>
    </li>
  );
}
